import { collection, getDocs, query, where } from "firebase/firestore";
import { getFunctions, httpsCallable } from "firebase/functions";
import { app, db } from "@/config/firebase";

type AdminClaimNotificationRecipient = {
  id: string;
  label: string;
  email: string;
  tokenCount: number;
};

type AdminClaimNotificationTarget = {
  adminUserId: string;
  title: string;
  body: string;
  data?: Record<string, string>;
};

function countTokens(rawTokens: unknown): number {
  if (!Array.isArray(rawTokens)) return 0;

  return rawTokens.filter((entry) => {
    if (typeof entry === "string") return entry.trim().length > 0;
    if (entry && typeof entry === "object" && "token" in entry) {
      const token = (entry as { token?: unknown }).token;
      return typeof token === "string" && token.trim().length > 0;
    }
    return false;
  }).length;
}

export async function getAdminClaimNotificationRecipients(): Promise<
  AdminClaimNotificationRecipient[]
> {
  const snapshot = await getDocs(
    query(collection(db, "users"), where("role", "==", "admin")),
  );
  const fallbackSnapshot = await getDocs(
    query(collection(db, "users"), where("profile.role", "==", "admin")),
  );

  const merged = new Map<string, AdminClaimNotificationRecipient>();

  for (const docSnap of [...snapshot.docs, ...fallbackSnapshot.docs]) {
    const data = docSnap.data() as Record<string, unknown>;
    merged.set(docSnap.id, {
      id: docSnap.id,
      label:
        (typeof data.displayName === "string" && data.displayName) ||
        (typeof data.username === "string" && data.username) ||
        (typeof data.email === "string" && data.email) ||
        docSnap.id,
      email: typeof data.email === "string" ? data.email : "",
      tokenCount: countTokens(data.fcmTokens),
    });
  }

  return [...merged.values()].sort((a, b) => a.label.localeCompare(b.label));
}

export async function sendAdminClaimNotification({
  adminUserId,
  title,
  body,
  data = {},
}: AdminClaimNotificationTarget): Promise<{ successCount: number; failureCount: number }> {
  const callable = httpsCallable<
    {
      userId: string;
      title: string;
      body: string;
      type: string;
      data?: Record<string, string>;
    },
    { successCount: number; failureCount: number; totalTokens: number }
  >(getFunctions(app, "us-central1"), "sendTestNotification");

  const result = await callable({
    userId: adminUserId,
    title,
    body,
    type: "claim_submitted_admin",
    data,
  });

  return {
    successCount: result.data.successCount,
    failureCount: result.data.failureCount,
  };
}
